<template>
  <div class="modern-detail-view pb-12">
    <ValidationObserver ref="observer" v-slot="{ handleSubmit, invalid, validated }">
      <!-- Header -->
      <v-row class="mb-8 align-center px-4 pt-4">
        <v-btn icon @click.stop="$router.go(-1)" class="mr-4 grey lighten-4 shadow-sm">
          <v-icon color="primary" small>fas fa-chevron-left</v-icon>
        </v-btn>
        <div class="d-flex align-center">
          <v-avatar color="primary" size="48" class="mr-4 shadow-sm border-white-sm">
            <v-icon color="white">fas fa-truck</v-icon>
          </v-avatar>
          <div>
            <div class="text-h4 font-weight-black primary--text lh-1">
              {{ id > 0 ? `Guía de Despacho #${id}` : 'Nueva Guía de Despacho' }}
            </div>
            <div class="mt-1 caption grey--text font-weight-bold uppercase tracking-widest">Documento interno de despacho</div>
          </div>
        </div>
        <v-spacer></v-spacer>
        <v-btn
          :color="invalid && validated ? 'error' : 'success'"
          class="font-weight-black shadow-sm px-8 h-44"
          @click="handleSubmit(confirm)"
          :loading="loading"
        >
          <v-icon left small>{{ invalid && validated ? 'fas fa-exclamation-triangle' : 'fas fa-save' }}</v-icon>
          {{ invalid && validated ? 'Corregir Errores' : 'Guardar Guía' }}
        </v-btn>
      </v-row>

      <!-- Banner de entrega -->
      <v-alert v-if="slip.status === 'delivered'" type="success" outlined class="mx-4 mb-6" dense>
        <div class="d-flex align-center">
         
          <div>
            <div class="font-weight-black">Guía entregada</div>
            <div class="caption">
              Recibido por: <strong>{{ slip.signed_by || '—' }}</strong>
              <span v-if="slip.delivered_at" class="ml-2 grey--text">· {{ slip.delivered_at | moment('DD/MM/YYYY HH:mm') }}</span>
            </div>
          </div>
        </div>
      </v-alert>

      <v-form @submit.prevent="handleSubmit(confirm)" @keydown.enter.prevent>
        <v-row>
          <!-- Sidebar -->
          <v-col cols="12" md="3">
            <!-- Info básica -->
            <v-card outlined class="modern-card shadow-sm pa-6 mb-6" :class="invalid && validated ? 'border-error-light' : ''">
              <div class="caption grey--text font-weight-black text-uppercase tracking-widest mb-6">Información del Documento</div>

              <div class="caption grey--text font-weight-black mb-1 ml-1">Vincular a OT</div>
              <v-text-field
                v-model="work_order"
                placeholder="Nro de Orden de Trabajo..."
                outlined dense
                class="modern-input mb-4"
                :append-icon="work_order_finded ? 'fas fa-check-circle' : 'fas fa-search'"
                :loading="loadingWorkOrder"
                @keydown="searchWorkOrder"
                :success="work_order_finded"
                :hint="alertHasSlips ? 'Ya existe una guía para esta OT' : ''"
                :persistent-hint="alertHasSlips"
              ></v-text-field>

              <div class="caption grey--text font-weight-black mb-1 ml-1">Nro Folio (opcional)</div>
              <v-text-field
                v-model="slip.document_id"
                placeholder="Folio manual..."
                outlined dense
                class="modern-input mb-4"
              ></v-text-field>

              <div class="caption grey--text font-weight-black mb-1 ml-1">Fecha de Despacho</div>
              <ValidationProvider name="Fecha" rules="required" v-slot="{ errors }">
                <v-text-field
                  v-model="slip.date_emited"
                  type="date"
                  outlined dense
                  class="modern-input"
                  :error-messages="errors"
                ></v-text-field>
              </ValidationProvider>
            </v-card>

            <!-- Receptor -->
            <v-card outlined class="modern-card shadow-sm pa-6 mb-6">
              <div class="caption grey--text font-weight-black text-uppercase tracking-widest mb-6">Datos del Receptor</div>

              <div class="caption grey--text font-weight-black mb-1 ml-1">{{ $store.getters.getInternational.tax_id_label }}</div>
              <ValidationProvider name="RUT" rules="required" v-slot="{ errors }">
                <v-text-field
                  v-model="slip.rutRecep"
                  outlined dense
                  class="modern-input mb-4"
                  :loading="loadingCustomer"
                  :error-messages="errors"
                  @keyup="getCustomerDataByRut"
                ></v-text-field>
              </ValidationProvider>

              <div class="caption grey--text font-weight-black mb-1 ml-1">Razón Social</div>
              <ValidationProvider name="Razon Social" rules="required" v-slot="{ errors }">
                <v-text-field
                  v-model="slip.rznSocRecep"
                  outlined dense
                  class="modern-input mb-4"
                  :error-messages="errors"
                ></v-text-field>
              </ValidationProvider>

              <div class="caption grey--text font-weight-black mb-1 ml-1">Giro Comercial</div>
              <v-text-field
                v-model="slip.giroRecep"
                outlined dense
                class="modern-input mb-4"
                hide-details
              ></v-text-field>

              <div class="caption grey--text font-weight-black mb-1 ml-1">Contacto</div>
              <v-select
                :items="contactList"
                item-text="email"
                return-object
                outlined dense
                placeholder="Elegir..."
                class="modern-input mb-4"
                @change="selectContact"
                hide-details
              ></v-select>

              <div class="caption grey--text font-weight-black mb-1 ml-1">Dirección</div>
              <v-select
                :items="addressList"
                item-text="name"
                return-object
                outlined dense
                placeholder="Elegir dirección..."
                class="modern-input mb-4"
                @change="selectAddress"
                hide-details
              ></v-select>

              <v-text-field
                v-model="slip.dirRecep"
                label="Dirección"
                outlined dense
                class="modern-input mb-4"
                hide-details
              ></v-text-field>

              <v-text-field
                v-model="slip.cmnaRecep"
                :label="$store.getters.getInternational.comune_label"
                outlined dense
                class="modern-input"
                hide-details
              ></v-text-field>

              <v-alert type="info" dense text v-if="alertCustomerNotInDB" class="mt-4 caption">
                Cliente no registrado en el sistema
              </v-alert>
            </v-card>

            <!-- Transporte -->
            <v-card outlined class="modern-card shadow-sm pa-6 mb-6">
              <div class="caption grey--text font-weight-black text-uppercase tracking-widest mb-6">Datos de Transporte</div>

              <div class="caption grey--text font-weight-black mb-1 ml-1">Patente Vehículo</div>
              <v-text-field
                v-model="slip.vehicle_plate"
                placeholder="AA-BB-00"
                outlined dense
                class="modern-input mb-4"
                hide-details
              ></v-text-field>

              <div class="caption grey--text font-weight-black mb-1 ml-1">Nombre Conductor</div>
              <v-text-field
                v-model="slip.driver_name"
                outlined dense
                class="modern-input mb-4"
                hide-details
              ></v-text-field>

              <div class="caption grey--text font-weight-black mb-1 ml-1">RUT Conductor</div>
              <v-text-field
                v-model="slip.driver_rut"
                outlined dense
                class="modern-input"
                hide-details
              ></v-text-field>
            </v-card>

            <!-- Notas -->
            <v-card outlined class="modern-card shadow-sm pa-6">
              <div class="caption grey--text font-weight-black text-uppercase tracking-widest mb-6">Observaciones</div>

              <div class="caption grey--text font-weight-black mb-1 ml-1">Glosa del documento</div>
              <v-text-field
                v-model="slip.glosa"
                placeholder="Referencia general..."
                outlined dense
                class="modern-input mb-4"
                hide-details
              ></v-text-field>

              <div class="caption grey--text font-weight-black mb-1 ml-1">Notas adicionales</div>
              <v-textarea
                v-model="slip.notes"
                placeholder="Instrucciones de entrega, condiciones, etc..."
                outlined dense
                rows="3"
                class="modern-input"
                hide-details
              ></v-textarea>
            </v-card>
          </v-col>

          <!-- Tabla de productos -->
          <v-col cols="12" md="9">
            <div class="mb-4 d-flex align-center">
              <v-avatar color="primary" size="32" class="mr-3 shadow-xs">
                <v-icon color="white" x-small>fas fa-boxes</v-icon>
              </v-avatar>
              <div class="caption grey--text font-weight-black text-uppercase tracking-widest">
                Detalle de Productos / Mercadería a Despachar
              </div>
              <v-chip v-if="sales_note" color="primary lighten-5" class="primary--text ml-4 font-weight-bold" small label>
                Cotización #{{ sales_note }}
              </v-chip>
              <v-spacer></v-spacer>
              <v-btn color="success" class="font-weight-black rounded-lg px-6 shadow-sm" @click="addLine">
                <v-icon left small>fas fa-plus</v-icon> Agregar Línea
              </v-btn>
            </div>

            <v-card outlined class="modern-card shadow-sm overflow-hidden mb-6">
              <v-data-table
                :headers="headers"
                :items="slip.details"
                item-key="nro_linea"
                hide-default-footer
                class="modern-table"
                :items-per-page="-1"
              >
                <template v-slot:no-data>
                  <div class="pa-10 text-center fill-width grey--text">
                    <v-icon size="48" color="grey lighten-3" class="mb-4">fas fa-boxes</v-icon>
                    <div class="body-1 font-weight-bold uppercase mb-2">No hay productos en la guía</div>
                    <div class="caption">Agregue líneas o vincule una OT para cargar los productos</div>
                  </div>
                </template>

                <template v-slot:item.sku="{ item }">
                  <v-text-field
                    v-model="item.sku"
                    outlined dense hide-details
                    class="modern-input-minimal font-weight-black primary--text"
                    @keyup="getProductDatabySKU(item)"
                  >
                    <template v-slot:append v-if="item.product < 0 && item.sku !== ''">
                      <v-tooltip bottom>
                        <template v-slot:activator="{ on, attrs }">
                          <v-icon x-small color="warning" v-bind="attrs" v-on="on">fas fa-exclamation-triangle</v-icon>
                        </template>
                        <span>Producto no encontrado en inventario</span>
                      </v-tooltip>
                    </template>
                  </v-text-field>
                </template>

                <template v-slot:item.name="{ item }">
                  <v-textarea
                    v-model="item.name"
                    rows="1"
                    auto-grow
                    outlined dense hide-details
                    class="modern-input-minimal body-2 py-2"
                  />
                </template>

                <template v-slot:item.qty="{ item }">
                  <v-text-field
                    type="number"
                    v-model.number="item.qty"
                    :min="0"
                    outlined dense hide-details
                    class="modern-input-minimal text-center"
                    style="max-width: 100%"
                  ></v-text-field>
                </template>

                <template v-slot:item.unit="{ item }">
                  <v-text-field
                    v-model="item.unit"
                    outlined dense hide-details
                    class="modern-input-minimal text-center"
                    style="max-width: 100%"
                  ></v-text-field>
                </template>

                <template v-slot:item.notes="{ item }">
                  <v-text-field
                    v-model="item.notes"
                    placeholder="Observación..."
                    outlined dense hide-details
                    class="modern-input-minimal"
                  ></v-text-field>
                </template>

                <template v-slot:item.actions="{ item }">
                  <v-btn icon small color="error" class="grey lighten-4" @click.stop="removeLine(item)">
                    <v-icon x-small>fas fa-trash</v-icon>
                  </v-btn>
                </template>
              </v-data-table>
            </v-card>
          </v-col>
        </v-row>
      </v-form>
    </ValidationObserver>

    <confirm
      v-model="showConfirm"
      :message="slip.work_order ? `Se vinculará a la OT #${slip.work_order}. ¿Confirmar?` : 'Se creará la guía sin vínculo a OT. ¿Confirmar?'"
      @ok="save"
      @cancel="showConfirm = false"
    />
  </div>
</template>

<script>
import { createPackingSlip, updatePackingSlip, getPackingSlip, getWorkOrderforPackingSlip } from '@/api/salesNotes'
import { getCustomer, getCustomerByRUT } from '@/api/customers'
import { getProductbySKU } from '@/api/products'
import notifications from '@/utils/notifications'
import { validaRUT } from '@/utils/rut_validator'

export default {
  name: 'CreatePackingSlip',
  data() {
    return {
      loading: false,
      showConfirm: false,
      id: -1,
      work_order: '',
      sales_note: '',
      loadingWorkOrder: false,
      work_order_finded: false,
      loadingCustomer: false,
      alertCustomerNotInDB: false,
      alertHasSlips: false,
      timer: null,
      contactList: [],
      addressList: [],
      slip: {
        document_id: '',
        date_emited: new Date().toISOString().substr(0, 10),
        rutRecep: '',
        rznSocRecep: '',
        giroRecep: '',
        contactRecep: '',
        dirRecep: '',
        cmnaRecep: '',
        glosa: '',
        notes: '',
        vehicle_plate: '',
        driver_name: '',
        driver_rut: '',
        work_order: null,
        customer: null,
        details: [],
        status: 'pending',
        signed_by: '',
        delivered_at: null,
      },
      headers: [
        { text: 'Código/SKU', align: 'start', sortable: false, value: 'sku', class: 'table-header', width: '110px' },
        { text: 'Descripción', align: 'start', sortable: false, value: 'name', class: 'table-header' },
        { text: 'Unidad', align: 'center', sortable: false, value: 'unit', class: 'table-header', width: '90px' },
        { text: 'Cantidad', align: 'center', sortable: false, value: 'qty', width: '120px', class: 'table-header' },
        { text: 'Observación', align: 'start', sortable: false, value: 'notes', width: '200px', class: 'table-header' },
        { text: '', align: 'end', sortable: false, value: 'actions', width: '50px', class: 'table-header' },
      ],
    }
  },
  methods: {
    addLine() {
      this.slip.details.push({
        nro_linea: this.slip.details.length + 1,
        product: -1,
        sku: '',
        name: '',
        unit: 'UN',
        qty: 0,
        price: 0,
        notes: '',
      })
    },
    removeLine(item) {
      this.slip.details = this.slip.details.filter(i => i.nro_linea !== item.nro_linea)
    },
    confirm() {
      if (this.slip.details.length <= 0) {
        notifications.showError('Deben existir elementos en la guía')
        return
      }
      this.showConfirm = true
    },
    async getCustomerData(customer_id) {
      this.loadingCustomer = true
      try {
        const data = await getCustomer(customer_id)
        this.slip.customer = data.id
        this.slip.rutRecep = data.rut
        this.slip.rznSocRecep = data.name
        this.slip.giroRecep = data.giro
        this.contactList = data.contacts
        if (data.contacts.length > 0) this.selectContact(data.contacts[0])
        this.addressList = data.addresses
        if (data.addresses.length > 0) this.selectAddress(data.addresses[0])
        this.alertCustomerNotInDB = false
      } catch (e) {
        notifications.showError('Error cargando datos del cliente')
      } finally {
        this.loadingCustomer = false
      }
    },
    getCustomerDataByRut() {
      if (this.timer) clearTimeout(this.timer)
      this.timer = setTimeout(async () => {
        if (validaRUT(this.slip.rutRecep)) {
          this.loadingCustomer = true
          try {
            const data = await getCustomerByRUT(this.slip.rutRecep)
            this.slip.customer = data.id
            this.slip.rznSocRecep = data.name
            this.slip.giroRecep = data.giro
            this.contactList = data.contacts
            if (data.contacts.length > 0) this.selectContact(data.contacts[0])
            this.addressList = data.addresses
            if (data.addresses.length > 0) this.selectAddress(data.addresses[0])
            this.alertCustomerNotInDB = false
          } catch (e) {
            notifications.showInfo('Cliente no encontrado en BBDD')
            this.alertCustomerNotInDB = true
          } finally {
            this.loadingCustomer = false
          }
        }
      }, 1000)
    },
    getProductDatabySKU(item) {
      if (this.timer) clearTimeout(this.timer)
      this.timer = setTimeout(async () => {
        try {
          const data = await getProductbySKU(item.sku)
          item.product = data.id
          item.name = data.name
          item.unit = data.sell_unit_m2 ? 'MTS2' : 'UN'
          item.qty = 1
        } catch (e) {
          item.product = -1
        }
      }, 1000)
    },
    selectContact(val) {
      const { first_name, last_name } = val
      this.slip.contactRecep = `${first_name} ${last_name}`
    },
    selectAddress(val) {
      const { street, number, comune } = val
      this.slip.dirRecep = `${street} ${number}`
      this.slip.cmnaRecep = comune
    },
    searchWorkOrder() {
      if (this.timer) clearTimeout(this.timer)
      this.timer = setTimeout(async () => {
        if (!this.work_order) return
        this.loadingWorkOrder = true
        try {
          const data = await getWorkOrderforPackingSlip(this.work_order)
          this.work_order_finded = true
          const { customer, title, products, id, additional_costs, totals, sales_note, has_packing_slips } = data
          this.alertHasSlips = has_packing_slips
          this.sales_note = sales_note
          this.slip.customer = customer.id
          this.slip.rutRecep = customer.rut
          this.slip.rznSocRecep = customer.name
          this.slip.giroRecep = customer.giro
          this.contactList = customer.contacts
          if (customer.contacts.length > 0) this.selectContact(customer.contacts[0])
          this.addressList = customer.addresses
          if (customer.addresses.length > 0) this.selectAddress(customer.addresses[0])
          this.slip.work_order = id
          this.slip.glosa = title
          this.slip.details = []

          products.forEach(p => {
            this.slip.details.push({
              nro_linea: this.slip.details.length + 1,
              product: p.id,
              sku: p.sku,
              name: p.name,
              unit: p.sell_unit_m2 ? 'MTS2' : 'UN',
              qty: p.sell_unit_m2 ? p.totalMts2 : p.qty,
              price: 0,
              notes: '',
            })
          })

          additional_costs.forEach(c => {
            this.slip.details.push({
              nro_linea: this.slip.details.length + 1,
              product: -1,
              sku: c.name.toUpperCase().slice(0, 4),
              name: c.name,
              unit: c.cost_unit,
              qty: c.unit === 1 ? totals.mts2 : c.qty,
              price: 0,
              notes: '',
            })
          })
        } catch (e) {
          notifications.showInfo('Orden de Trabajo no encontrada')
          this.work_order_finded = false
        } finally {
          this.loadingWorkOrder = false
        }
      }, 800)
    },
    async save() {
      this.showConfirm = false
      if (this.slip.details.length <= 0) {
        notifications.showError('Deben existir items en la guía')
        return
      }
      this.loading = true
      const payload = JSON.parse(JSON.stringify(this.slip))
      if (!payload.customer) delete payload.customer
      if (!payload.work_order) delete payload.work_order

      try {
        if (this.id > 0) {
          payload.id = this.id
          await updatePackingSlip(payload)
          notifications.showSuccess('Guía actualizada con éxito')
        } else {
          await createPackingSlip(payload)
          notifications.showSuccess('Guía creada con éxito')
        }
        this.$router.push('/sales/packing-slips')
      } catch (e) {
        notifications.showError('Error al guardar la guía: ' + e)
      } finally {
        this.loading = false
      }
    },
  },
  async mounted() {
    if (this.$route.params.id > 0) {
      this.id = parseInt(this.$route.params.id)
      try {
        const data = await getPackingSlip(this.id)
        this.slip = {
          document_id: data.document_id || '',
          date_emited: data.date_emited || new Date().toISOString().substr(0, 10),
          rutRecep: data.rutRecep || '',
          rznSocRecep: data.rznSocRecep || '',
          giroRecep: data.giroRecep || '',
          contactRecep: data.contactRecep || '',
          dirRecep: data.dirRecep || '',
          cmnaRecep: data.cmnaRecep || '',
          glosa: data.glosa || '',
          notes: data.notes || '',
          vehicle_plate: data.vehicle_plate || '',
          driver_name: data.driver_name || '',
          driver_rut: data.driver_rut || '',
          work_order: data.work_order || null,
          customer: data.customer || null,
          details: data.details || [],
          status: data.status || 'pending',
          signed_by: data.signed_by || '',
          delivered_at: data.delivered_at || null,
        }
        if (data.work_order) {
          this.work_order = data.work_order
          this.work_order_finded = true
        }
        if (data.sales_note) this.sales_note = data.sales_note
        if (data.customer) {
          this.getCustomerData(data.customer)
        }
      } catch (e) {
        notifications.showError('Error cargando guía')
      }
    }
  },
}
</script>

<style scoped>
.modern-detail-view { background-color: #f4f7f9; min-height: 100vh; }
.modern-card { border-radius: 12px !important; border: 1px solid rgba(0,0,0,0.05) !important; background-color: white !important; transition: all 0.3s ease; }
.shadow-sm { box-shadow: 0 4px 12px rgba(0,0,0,0.04) !important; }
.shadow-xs { box-shadow: 0 1px 3px rgba(0,0,0,0.05) !important; }
.border-white-sm { border: 2px solid white !important; }
.border-error-light { border: 1px solid rgba(var(--v-error-base), 0.3) !important; }
.lh-1 { line-height: 1 !important; }
.gap-3 { gap: 12px; }
.uppercase { text-transform: uppercase; }
.tracking-widest { letter-spacing: 1.8px; }
.h-44 { height: 44px !important; }

.modern-input >>> .v-input__control fieldset { border-radius: 8px !important; border-color: #e0e4e8 !important; }
.modern-input >>> input { font-size: 0.9rem !important; font-weight: 500 !important; color: #1a1f2e !important; }

.modern-input-minimal >>> .v-input__control fieldset { border-radius: 6px !important; border-color: #cbd5e1 !important; }
.modern-input-minimal >>> input, .modern-input-minimal >>> textarea { font-size: 0.85rem !important; padding: 4px 6px !important; }

.modern-table >>> thead th { background-color: #f8fbfc !important; height: 52px !important; text-transform: uppercase; font-size: 0.75rem !important; letter-spacing: 1px; font-weight: 900 !important; color: #607d8b !important; }
.modern-table >>> td { height: 64px !important; border-bottom: 1px solid #f0f3f5 !important; }
</style>
